import React from 'react';
import ProductPage from '../components/ProductPage';
import { AmmTaskFinder } from '../components/AmmTaskFinder';
import { getBoeingCoverage } from '../data/acousticCameras';

const FOTRIC_IMAGES = [
  'https://static.wixstatic.com/media/0fa391_949dee3c5b9842539c8efd8cf74acd56~mv2.png/v1/fill/w_720,h_720,al_c,q_85,enc_auto/0fa391_949dee3c5b9842539c8efd8cf74acd56~mv2.png',
  'https://static.wixstatic.com/media/0fa391_145b721792374b60866b469e5148eb42~mv2.png/v1/fill/w_720,h_720,al_c,q_85,enc_auto/0fa391_145b721792374b60866b469e5148eb42~mv2.png',
  'https://static.wixstatic.com/media/0fa391_5ff90e075d7d424db31ab1651ec688f0~mv2.png/v1/fill/w_540,h_810,al_c,q_85,enc_auto/0fa391_5ff90e075d7d424db31ab1651ec688f0~mv2.png',
];

const AcousticCameraH4MiniPage = ({ onRequestDemo, onRequestQuote }) => {
  const productData = {
    productName: 'FOTRIC H4mini Acoustic Imaging Camera',
    tagline: 'Compact Acoustic Imaging — Find Leaks in Seconds',
    description:
      'The FOTRIC H4mini is a lightweight, handheld acoustic imaging camera engineered to visualize ultrasonic sound the human ear cannot hear. With a 112 MEMS digital microphone array, a 13 MP industrial camera, and a real-time 3.5" IPS touch display, the H4mini pinpoints compressed air leaks, vacuum system losses, tire leaks, and abnormal vibration from loose components without shutting down the equipment. At just 770 g and rated IP54, it is built for fast, one-handed inspections across MRO floors, hangars, and flight-line operations.',
    image: FOTRIC_IMAGES[0],
    images: FOTRIC_IMAGES,
    imageContain: true,
    features: [
      {
        title: '112 MEMS Digital Microphone Array',
        description:
          'High-density spiral array captures subtle ultrasonic signals from 2 kHz to 100 kHz — far beyond the range of human hearing or traditional stethoscopes.',
      },
      {
        title: 'Beamforming Algorithm + Acoustic Overlay',
        description:
          'Real-time beamforming isolates sound sources by direction and overlays a heatmap on the 13 MP optical image, so leaks and faults show up as an unmistakable hotspot on the display.',
      },
      {
        title: 'Multiple Imaging Modes',
        description:
          'Switch between single-source, multi-source, and holographic modes to adapt to the noise complexity of the inspection scene — busy shop floor, quiet cabin, or open ramp.',
      },
      {
        title: 'Flexible Capture Modes',
        description:
          'Holographic image capture, holographic video recording, and time-lapse capture so inspectors can document findings end-to-end without swapping tools.',
      },
      {
        title: 'Acoustic Image Delay (T-FFTD®)',
        description:
          'Captures instantaneous sound events and holds them in the real-time acoustic image longer — ideal for intermittent leaks, chatter, and transient discharges.',
      },
      {
        title: 'Acoustic Image Focus',
        description:
          'Masks surrounding areas and locks the display onto a specific region of interest — cutting through busy backgrounds during ramp and hangar inspections.',
      },
      {
        title: 'Precise SPL Measurement',
        description:
          'Combines holographic imaging with decibel measurement for industrial-level sound source tracking and signal diagnosis on-device.',
      },
      {
        title: 'On-Device Report Generation',
        description:
          'Generates acoustic image reports directly on the camera — no laptop, no export chain — so findings can be shared before you leave the aircraft.',
      },
      {
        title: '≥4 Hour Runtime with Field-Replaceable Battery',
        description:
          'Removable 5000 mAh lithium battery for continuous inspections, with quick swap for full-shift coverage.',
      },
      {
        title: 'IP54 Rugged Enclosure',
        description:
          'Dust and splash resistant for real-world MRO environments — not a benchtop instrument.',
      },
      {
        title: 'QR Code Scanning + Favorites',
        description:
          'Scan asset QR/bar codes to auto-tag reports; flag key images as favorites for fast retrieval and filtering in the on-device gallery.',
      },
    ],
    specifications: [
      { label: 'Microphone Channels', value: '112 MEMS digital microphones' },
      { label: 'Acoustic Image FOV', value: '66° × 52°' },
      { label: 'Sound Sampling Rate', value: '200 kHz' },
      { label: 'Acoustic Refresh Rate', value: '25 Hz' },
      { label: 'Frequency Range', value: '2 – 100 kHz' },
      { label: 'Operating Distance', value: '0.3 – 100 m' },
      { label: 'Detection Mode', value: 'Leakage mode — on-device leakage level display' },
      { label: 'Image Modes', value: 'Single-source, multi-source, hologram' },
      { label: 'Display', value: '3.5" 640 × 480 IPS LCD touchscreen' },
      { label: 'Digital Camera', value: '13 Megapixel industrial-grade' },
      { label: 'Battery', value: '3.6 V, 5000 mAh rechargeable Li-ion, field-replaceable' },
      { label: 'Battery Runtime', value: '≥ 4 hours continuous' },
      { label: 'Weight', value: '770 g (1.70 lb)' },
      { label: 'Dimensions', value: '243 × 95 × 150 mm' },
      { label: 'Enclosure Rating', value: 'IP54' },
      { label: 'On-Device Report Generation', value: 'Yes — acoustic image reports on-camera' },
      { label: 'Storage', value: '32 GB TF card included' },
      { label: 'Supported Languages', value: 'English, French, German, Italian, Japanese, Korean, Portuguese, Spanish, Thai, Traditional Chinese' },
    ],
    applications: [
      'Compressed air system leak detection across MRO shops',
      'Vacuum system leak localization on aircraft ground-support equipment',
      'Tire air leak detection — precise, no soap spray required',
      'Loose fastener and joint vibration detection during operation',
      'Bleed-air and pneumatic system leak inspections',
      'Hydraulic and fuel line acoustic surveys',
      'Cabin pressurization pre-flight troubleshooting',
      'Line-side spot checks during turnarounds and AOG response',
      'Facility compressed air audits at hangar and MRO utility rooms',
      'Loose panel and skin-fastener vibration walks',
    ],
    partNumber: 'F00042',
    metaDescription: 'FOTRIC H4mini: 770 g acoustic imaging camera with 112 MEMS mics, 2–100 kHz, leak and partial-discharge imaging. Boeing COM-18702 equivalent.',
    boeingToolTag: 'COM-18702 (equivalent)',
    boeingCoverage: getBoeingCoverage('h4mini'),
    compareCameraId: 'h4mini',
  };

  return <ProductPage whereToBuy buyNow="F00042" {...productData} extraSections={<AmmTaskFinder tool="COM-18702" />} onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />;
};

export default AcousticCameraH4MiniPage;
